import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { Button } from '@/components/ui/button';
import { Label } from '@/components/ui/label';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Save, Eye, FileText } from 'lucide-react';
import './LegalEditor.css';

const API = process.env.REACT_APP_BACKEND_URL + '/api';

const LANGUAGES = {
  de: 'Deutsch',
  en: 'English',
  es: 'Español',
  fr: 'Français',
  it: 'Italiano'
};

const LegalEditor = () => {
  const [selectedLanguage, setSelectedLanguage] = useState('de');
  const [documentType, setDocumentType] = useState('privacy'); // 'privacy', 'terms', or 'imprint'
  const [content, setContent] = useState('');
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    loadDocument();
  }, [selectedLanguage, documentType]);

  const loadDocument = async () => {
    setLoading(true);
    try {
      let endpoint;
      if (documentType === 'privacy') {
        endpoint = `${API}/legal/privacy-policy?language=${selectedLanguage}`;
      } else if (documentType === 'terms') {
        endpoint = `${API}/legal/terms?language=${selectedLanguage}`;
      } else {
        endpoint = `${API}/legal/imprint?language=${selectedLanguage}`;
      }
      
      const response = await axios.get(endpoint);
      setContent(response.data.content || getDefaultTemplate());
    } catch (error) {
      console.error('Error loading document:', error);
      setContent(getDefaultTemplate());
    } finally {
      setLoading(false);
    }
  };

  const saveDocument = async () => {
    if (!content.trim()) {
      toast.error('Inhalt darf nicht leer sein');
      return;
    }

    setSaving(true);
    try {
      let endpoint;
      if (documentType === 'privacy') {
        endpoint = `${API}/admin/legal/privacy-policy`;
      } else if (documentType === 'terms') {
        endpoint = `${API}/admin/legal/terms`;
      } else {
        endpoint = `${API}/admin/legal/imprint`;
      }
      
      await axios.post(endpoint, {
        language: selectedLanguage,
        content: content
      });

      toast.success('Erfolgreich gespeichert!');
    } catch (error) {
      console.error('Error saving document:', error);
      toast.error('Fehler beim Speichern');
    } finally {
      setSaving(false);
    }
  };

  const getDefaultTemplate = () => {
    if (documentType === 'privacy') {
      return `<h2>Datenschutzerklärung</h2>
<p><strong>Verantwortlicher:</strong><br>
[Ihr Name/Firma]<br>
[Adresse]<br>
[Email]</p>

<h3>1. Welche Daten sammeln wir?</h3>
<p>Wir sammeln folgende personenbezogene Daten:</p>
<ul>
  <li>Email-Adresse (bei Registrierung)</li>
  <li>Benutzername</li>
  <li>Debattendaten und Argumente</li>
  <li>Nutzungsstatistiken</li>
</ul>

<h3>2. Warum sammeln wir diese Daten?</h3>
<p>Wir nutzen Ihre Daten für:</p>
<ul>
  <li>Account-Verwaltung</li>
  <li>Bereitstellung der Debattenfunktionen</li>
  <li>Verbesserung unseres Services</li>
</ul>

<h3>3. Ihre Rechte</h3>
<p>Sie haben das Recht auf:</p>
<ul>
  <li>Auskunft über Ihre gespeicherten Daten</li>
  <li>Berichtigung fehlerhafter Daten</li>
  <li>Löschung Ihrer Daten</li>
  <li>Datenübertragbarkeit</li>
</ul>

<h3>4. Kontakt</h3>
<p>Bei Fragen zum Datenschutz kontaktieren Sie uns unter: [Email]</p>`;
    } else {
const getDefaultTemplate = () => {
    if (documentType === 'privacy') {
      return `<h2>Datenschutzerklärung</h2>
<p><strong>Verantwortlicher:</strong><br>
[Ihr Name/Firma]<br>
[Adresse]<br>
[Email]</p>

<h3>1. Welche Daten sammeln wir?</h3>
<p>Wir sammeln folgende personenbezogene Daten:</p>
<ul>
  <li>Email-Adresse (bei Registrierung)</li>
  <li>Benutzername</li>
  <li>Debattendaten und Argumente</li>
  <li>Nutzungsstatistiken</li>
</ul>

<h3>2. Warum sammeln wir diese Daten?</h3>
<p>Wir nutzen Ihre Daten für:</p>
<ul>
  <li>Account-Verwaltung</li>
  <li>Bereitstellung der Debattenfunktionen</li>
  <li>Verbesserung unseres Services</li>
</ul>

<h3>3. Ihre Rechte</h3>
<p>Sie haben das Recht auf:</p>
<ul>
  <li>Auskunft über Ihre gespeicherten Daten</li>
  <li>Berichtigung fehlerhafter Daten</li>
  <li>Löschung Ihrer Daten</li>
  <li>Datenübertragbarkeit</li>
</ul>

<h3>4. Kontakt</h3>
<p>Bei Fragen zum Datenschutz kontaktieren Sie uns unter: [Email]</p>`;
    } else if (documentType === 'terms') {
      return `<h2>Nutzungsbedingungen</h2>

<h3>1. Akzeptanz der Bedingungen</h3>
<p>Mit der Nutzung dieser Plattform akzeptieren Sie diese Nutzungsbedingungen.</p>

<h3>2. Nutzer-Account</h3>
<p>Sie sind verantwortlich für:</p>
<ul>
  <li>Die Geheimhaltung Ihres Passworts</li>
  <li>Alle Aktivitäten unter Ihrem Account</li>
  <li>Die Richtigkeit Ihrer Angaben</li>
</ul>

<h3>3. Verbotene Inhalte</h3>
<p>Folgende Inhalte sind nicht erlaubt:</p>
<ul>
  <li>Beleidigungen und Hassrede</li>
  <li>Illegale Inhalte</li>
  <li>Spam oder Werbung</li>
</ul>

<h3>4. Haftungsausschluss</h3>
<p>Die Plattform wird "wie besehen" bereitgestellt. Wir übernehmen keine Garantie für Verfügbarkeit oder Fehlerfreiheit.</p>

<h3>5. Änderungen</h3>
<p>Wir behalten uns das Recht vor, diese Bedingungen jederzeit zu ändern.</p>`;
    } else {
      return `<h2>Impressum</h2>
<p><strong>Angaben gemäß § 5 TMG:</strong></p>
<p>[Ihr Name/Firma]<br>
[Adresse]<br>
[PLZ Ort]</p>

<h3>Kontakt</h3>
<p>E-Mail: [Email-Adresse]<br>
Website: https://debatefy.online</p>

<h3>Verantwortlich für den Inhalt nach § 55 Abs. 2 RStV:</h3>
<p>[Name]<br>
[Adresse]</p>`;
    }
  };
      return `<h2>Nutzungsbedingungen</h2>

<h3>1. Akzeptanz der Bedingungen</h3>
<p>Mit der Nutzung dieser Plattform akzeptieren Sie diese Nutzungsbedingungen.</p>

<h3>2. Nutzer-Account</h3>
<p>Sie sind verantwortlich für:</p>
<ul>
  <li>Die Geheimhaltung Ihres Passworts</li>
  <li>Alle Aktivitäten unter Ihrem Account</li>
  <li>Die Richtigkeit Ihrer Angaben</li>
</ul>

<h3>3. Verbotene Inhalte</h3>
<p>Folgende Inhalte sind nicht erlaubt:</p>
<ul>
  <li>Beleidigungen und Hassrede</li>
  <li>Illegale Inhalte</li>
  <li>Spam oder Werbung</li>
</ul>

<h3>4. Haftungsausschluss</h3>
<p>Die Plattform wird "wie besehen" bereitgestellt. Wir übernehmen keine Garantie für Verfügbarkeit oder Fehlerfreiheit.</p>

<h3>5. Änderungen</h3>
<p>Wir behalten uns das Recht vor, diese Bedingungen jederzeit zu ändern.</p>`;
    }
  };

  const previewDocument = () => {
    const newWindow = window.open('', '_blank');
    newWindow.document.write(`
      <!DOCTYPE html>
      <html>
        <head>
          <title>Vorschau</title>
          <style>
            body {
              font-family: Arial, sans-serif;
              max-width: 900px;
              margin: 40px auto;
              padding: 20px;
              line-height: 1.8;
            }
            h2 { color: #108ADF; font-size: 32px; }
            h3 { color: #666; font-size: 22px; margin-top: 25px; }
            p { margin-bottom: 15px; }
            ul { margin-left: 30px; }
            li { margin-bottom: 8px; }
          </style>
        </head>
        <body>${content}</body>
      </html>
    `);
    newWindow.document.close();
  };

  return (
    <div className="legal-editor-container">
      <Card>
        <CardHeader>
          <CardTitle>
            <FileText className="inline mr-2" size={24} />
            Rechtliche Dokumente verwalten
          </CardTitle>
          <CardDescription>
            Bearbeiten Sie Datenschutzerklärung und Nutzungsbedingungen in allen Sprachen
          </CardDescription>
        </CardHeader>
        <CardContent>
          {/* Controls */}
          <div className="editor-controls">
            <div className="control-group">
              <Label>Dokument</Label>
              <Select value={documentType} onValueChange={setDocumentType}>
                <SelectTrigger className="w-[250px]">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="privacy">Datenschutzerklärung</SelectItem>
                  <SelectItem value="terms">Nutzungsbedingungen</SelectItem>
                  <SelectItem value="imprint">Impressum</SelectItem>
                </SelectContent>
              </Select>
            </div>

            <div className="control-group">
              <Label>Sprache</Label>
              <Select value={selectedLanguage} onValueChange={setSelectedLanguage}>
                <SelectTrigger className="w-[200px]">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  {Object.entries(LANGUAGES).map(([code, name]) => (
                    <SelectItem key={code} value={code}>{name}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
          </div>

          {/* Editor Tabs */}
          <Tabs defaultValue="edit" className="editor-tabs">
            <TabsList>
              <TabsTrigger value="edit">Bearbeiten</TabsTrigger>
              <TabsTrigger value="preview">Vorschau</TabsTrigger>
            </TabsList>

            <TabsContent value="edit">
              {loading ? (
                <div className="editor-loading">Lädt...</div>
              ) : (
                <>
                  <div className="editor-info">
                    <p>💡 Sie können HTML-Tags verwenden: &lt;h2&gt;, &lt;h3&gt;, &lt;p&gt;, &lt;ul&gt;, &lt;li&gt;, &lt;strong&gt;</p>
                  </div>
                  <textarea
                    className="legal-textarea"
                    value={content}
                    onChange={(e) => setContent(e.target.value)}
                    placeholder="Inhalt hier eingeben..."
                    rows={25}
                  />
                </>
              )}
            </TabsContent>

            <TabsContent value="preview">
              <div 
                className="legal-preview"
                dangerouslySetInnerHTML={{ __html: content }}
              />
            </TabsContent>
          </Tabs>

          {/* Action Buttons */}
          <div className="editor-actions">
            <Button
              onClick={previewDocument}
              variant="outline"
              disabled={loading}
            >
              <Eye size={18} className="mr-2" />
              In neuem Tab öffnen
            </Button>
            
            <Button
              onClick={saveDocument}
              disabled={saving || loading || !content.trim()}
            >
              <Save size={18} className="mr-2" />
              {saving ? 'Speichert...' : 'Speichern'}
            </Button>
          </div>
        </CardContent>
      </Card>
    </div>
  );
};

export default LegalEditor;
